// src/pages/Hub/components/DetailCanvas.js
//
// Right-hand drawer. Three modes:
//   • kind 'client'  → date picker + that client's jobs on the chosen day in ANY
//     status (the original Client Locator flow, now powered by /ops-hub/jobs).
//   • kind 'job'     → details for a single job picked from the feed.
//   • kind 'report'  → a Reports & Data tile: a breakdown built from the already-
//     loaded /ops-hub/reports payload, plus a real-record drill-down for the
//     high-value tiles fetched from /ops-hub/reports/:kind:
//       finance     → petty-cash + purchase requests
//       fsr         → field-service reports (all regions); click one to see its full
//                     detail (inspection findings, notes, panels/cash, photos)
//       attendance  → who signed in (range) + roster members who didn't
//       compliance  → roster who acknowledged vs who didn't
//       ehs         → roster who filed the checklist vs who didn't
//     The records fetch is guarded — a 404 (pre-deploy) just leaves the breakdown.

import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import {
  X, CalendarDays, Loader2, Inbox, MapPin, Users, Clock, Building2, Home, Globe2,
  ClipboardCheck, Wallet, UserCheck, Radio, ShieldCheck, CheckSquare, ChevronRight, ArrowLeft,
} from 'lucide-react';
import { fetchJobs, fetchReportItems, fetchFsrDetail } from '../lib/opsHubApi';
import { regionMeta, statusInfo, fmtMoney, C } from '../theme';

// SERVER_BASE = API base without the trailing /api (image paths are server-relative).
const IMG_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace('/api', '');
const fullImg = (u) => (/^(https?:|data:)/i.test(u) ? u : `${IMG_BASE}${u}`);

const DateInput = React.forwardRef(({ value, onClick }, ref) => (
  <button type="button" className="hub-date-btn" onClick={onClick} ref={ref}>
    <CalendarDays size={15} /> <span>{value}</span>
  </button>
));
DateInput.displayName = 'DateInput';

const RegionIcon = ({ region, size = 16 }) => {
  if (region === 'commercial') return <Building2 size={size} />;
  if (region === 'mideast') return <Globe2 size={size} />;
  return <Home size={size} />;
};

const REPORT_META = {
  fsr: { title: 'FSR Reports', color: C.cyan, Icon: ClipboardCheck },
  finance: { title: 'Finance', color: C.green, Icon: Wallet },
  attendance: { title: 'Attendance', color: C.amber, Icon: UserCheck },
  trackers: { title: 'Trackers', color: C.violet, Icon: Radio },
  compliance: { title: 'Compliance', color: '#22D3EE', Icon: ShieldCheck },
  ehs: { title: 'EHS', color: C.green, Icon: CheckSquare },
};
const RECORD_KEYS = ['finance', 'fsr', 'attendance', 'compliance', 'ehs'];

const fmtTime = (t) => (t ? format(new Date(t), 'h:mm a') : null);
const fmtDate = (t) => (t ? format(new Date(t), 'MMM d, yyyy') : null);
const fmtDateTime = (t) => (t ? format(new Date(t), 'MMM d, yyyy, h:mm a') : null);
// Date-only values come through as 'YYYY-MM-DD'; everything else is a full timestamp.
const fmtStamp = (v) => (!v ? null : (/^\d{4}-\d{2}-\d{2}$/.test(String(v)) ? fmtDate(v) : fmtDateTime(v)));
const roleLabel = (u) => (u.is_lead ? 'Supervisor' : 'Field officer');

function JobCard({ job }) {
  const si = statusInfo(job.status);
  const start = fmtTime(job.startedAt);
  const end = fmtTime(job.completedAt);
  return (
    <motion.div
      className="hub-job" style={{ '--c': si.color }}
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      whileHover={{ y: -2 }}
    >
      <span className="hub-job-bar" style={{ background: si.color, boxShadow: `0 0 12px ${si.color}` }} />
      <div className="hub-job-head">
        <span className="hub-job-status" style={{ color: si.color }}>
          <span className="hub-job-dot" style={{ background: si.color }} /> {si.label}
        </span>
        {job.scheduledDate && <span style={{ fontSize: 11.5, color: C.muted, fontFamily: "'DM Mono', monospace" }}>{job.scheduledDate}</span>}
      </div>
      <div className="hub-job-title">{job.siteName || job.clientName || 'Job'}</div>
      <div className="hub-job-meta">
        {(job.city || job.state) && <span><MapPin size={13} /> {[job.city, job.state].filter(Boolean).join(', ')}</span>}
        {job.team && <span><Users size={13} /> {job.team}</span>}
        {start && <span><Clock size={13} /> {start}{end ? ` – ${end}` : ''}</span>}
      </div>
    </motion.div>
  );
}

// ── A two-column key/value line (breakdown + FSR fields). `flag` highlights a concern.
function DlRow({ k, dot, n, v, total, flag }) {
  return (
    <div className={`hub-dl-row${total ? ' is-total' : ''}`}>
      <span className="hub-dl-k">
        {dot && <span className="hub-region-dot" style={{ background: dot, color: dot }} />}
        {k}{n != null && <span className="hub-dl-n">×{n}</span>}
      </span>
      <span className="hub-dl-v" style={flag ? { color: C.red } : undefined}>{v}{flag ? ' ⚠' : ''}</span>
    </div>
  );
}

// ── A single drill-down record line; clickable when given an onClick.
function Rec({ color, title, sub, amount, badge, onClick }) {
  const clickable = !!onClick;
  return (
    <div
      className={`hub-rec${clickable ? ' is-click' : ''}`}
      onClick={onClick || undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <span className="hub-rec-dot" style={{ background: color }} />
      <div className="hub-rec-main">
        <div className="hub-rec-title">{title}</div>
        {sub && <div className="hub-rec-sub">{sub}</div>}
      </div>
      <div className="hub-rec-side">
        {amount != null && <div className="hub-rec-amt">{amount}</div>}
        {badge && <div className="hub-rec-status" style={{ color }}>{badge}</div>}
      </div>
      {clickable && <ChevronRight size={15} className="hub-rec-go" />}
    </div>
  );
}

const Empty = ({ children }) => <div className="hub-dl-empty">{children}</div>;

// ── One FSR's full detail (inspection findings, notes, panels/cash, photos).
function FsrDetailView({ item, detail, loading, error, onBack }) {
  const meta = regionMeta(item.region);
  return (
    <div className="hub-detail">
      <button className="hub-back" onClick={onBack}><ArrowLeft size={15} /> Back to reports</button>
      <div className="hub-fsr-head">
        <span className="hub-rec-dot" style={{ background: meta.color }} />
        <div>
          <div className="hub-fsr-title">{item.clientName || item.siteName || 'Field service report'}</div>
          <div className="hub-fsr-sub">{[meta.label, item.siteName && item.clientName ? item.siteName : null, fmtDate(item.date)].filter(Boolean).join(' · ')}</div>
        </div>
      </div>

      {loading ? (
        <div className="hub-feed-loading"><Loader2 size={20} className="hub-spin" /> Loading report…</div>
      ) : error ? (
        <div className="hub-dl-note">Report detail isn’t available — the latest backend may not be deployed yet.</div>
      ) : !detail ? (
        <div className="hub-dl-note">No detail found for this report.</div>
      ) : (
        <>
          {(detail.team || detail.submittedBy || detail.panelsCleaned != null || detail.cashCollected) && (
            <div className="hub-dl" style={{ marginTop: 2 }}>
              {detail.team && <DlRow k="Team" v={detail.team} />}
              {detail.submittedBy && <DlRow k="Submitted by" v={detail.submittedBy} />}
              {detail.panelsCleaned != null && <DlRow k="Panels cleaned" v={detail.panelsCleaned} />}
              {detail.cashCollected && <DlRow k="Cash collected" v={detail.cashCollected} />}
            </div>
          )}

          {detail.timestamps && detail.timestamps.length > 0 && (
            <>
              <div className="hub-dsub">Timeline</div>
              <div className="hub-dl">
                {detail.timestamps.map((t, i) => <DlRow key={i} k={t.label} v={fmtStamp(t.value)} />)}
              </div>
            </>
          )}

          {detail.findings && detail.findings.length > 0 && (
            <>
              <div className="hub-dsub">Inspection</div>
              <div className="hub-dl">
                {detail.findings.map((f, i) => <DlRow key={i} k={f.label} v={f.value} flag={f.flag} />)}
              </div>
            </>
          )}

          {(detail.workDone || detail.notes) && (
            <>
              <div className="hub-dsub">Notes</div>
              {detail.workDone && <div className="hub-note-block"><b>Work done:</b> {detail.workDone}</div>}
              {detail.notes && <div className="hub-note-block">{detail.notes}</div>}
            </>
          )}

          {detail.images && detail.images.length > 0 ? (
            <>
              <div className="hub-dsub">Photos<span className="hub-dsub-n">{detail.images.length}</span></div>
              <div className="hub-img-grid">
                {detail.images.map((im, i) => {
                  const src = fullImg(im.url);
                  return (
                    <a key={i} className="hub-img" href={src} target="_blank" rel="noreferrer" title={im.label}>
                      <img src={src} alt={im.label} loading="lazy" onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
                      <span className="hub-img-cap">{im.label}</span>
                    </a>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="hub-dl-note">No photos attached to this report.</div>
          )}
        </>
      )}
    </div>
  );
}

function ReportBody({ rkey, reports, recs, recLoading, recError, onOpenFsr }) {
  const fsr = reports?.fsr || { total: 0, byRegion: {} };
  const petty = reports?.finance?.pettyCash || [];
  const purchases = reports?.finance?.purchases || [];
  const att = reports?.attendance || {};
  const trackers = reports?.trackers || { samples: 0, jobs: 0 };

  // ── Breakdown (always, from the already-loaded payload) ───────────────────
  let breakdown = null;
  if (rkey === 'fsr') {
    const entries = Object.entries(fsr.byRegion);
    breakdown = (
      <>
        <div className="hub-dsub">By region</div>
        <div className="hub-dl">
          {entries.length === 0 && <Empty>None in range.</Empty>}
          {entries.map(([k, v]) => <DlRow key={k} k={regionMeta(k).label} dot={regionMeta(k).color} v={v} />)}
          <DlRow k="Total" v={fsr.total} total />
        </div>
      </>
    );
  } else if (rkey === 'finance') {
    breakdown = (
      <>
        <div className="hub-dsub">Petty cash by status</div>
        <div className="hub-dl">
          {petty.length === 0 && <Empty>None in range.</Empty>}
          {petty.map((r, i) => <DlRow key={i} k={statusInfo(r.status).label} n={r.n} v={`$${fmtMoney(r.amt)}`} />)}
        </div>
        <div className="hub-dsub">Purchases by status</div>
        <div className="hub-dl">
          {purchases.length === 0 && <Empty>None in range.</Empty>}
          {purchases.map((r, i) => <DlRow key={i} k={statusInfo(r.status).label} n={r.n} v={`$${fmtMoney(r.amt)}`} />)}
        </div>
      </>
    );
  } else if (rkey === 'attendance') {
    breakdown = (
      <div className="hub-dl">
        <DlRow k="Signed in" dot={C.green} v={att.present ?? 0} />
        <DlRow k="Not signed in" dot={C.amber} v={att.notSignedIn ?? 0} />
        <DlRow k="C&I roster" v={att.roster ?? 0} total />
      </div>
    );
  } else if (rkey === 'compliance' || rkey === 'ehs') {
    const m = reports?.[rkey] || {};
    const done = m.done ?? 0;
    const roster = m.roster ?? 0;
    breakdown = (
      <div className="hub-dl">
        <DlRow k="Completed" dot={C.green} v={done} />
        <DlRow k="Not completed" dot={C.red} v={Math.max(0, roster - done)} />
        <DlRow k="C&I roster" v={roster} total />
      </div>
    );
  } else { // trackers
    breakdown = (
      <div className="hub-dl">
        <DlRow k="Jobs with telemetry" v={(trackers.jobs || 0).toLocaleString()} />
        <DlRow k="Telemetry samples" v={(trackers.samples || 0).toLocaleString()} />
      </div>
    );
  }

  // ── Records (drill-down) ──────────────────────────────────────────────────
  let records = null;
  if (RECORD_KEYS.includes(rkey)) {
    let list = null;
    if (recLoading) {
      list = <div className="hub-feed-loading"><Loader2 size={20} className="hub-spin" /> Loading records…</div>;
    } else if (recError) {
      list = <div className="hub-dl-note">Detailed records will appear once the latest backend is deployed.</div>;
    } else if (rkey === 'finance') {
      const pc = recs?.pettyCash || [];
      const pu = recs?.purchases || [];
      list = (
        <>
          <div className="hub-dsub">Petty cash requests<span className="hub-dsub-n">{pc.length}</span></div>
          {pc.length === 0 ? <Empty>None in range.</Empty> : pc.map((p) => (
            <Rec
              key={`pc${p.id}`} color={statusInfo(p.status).color}
              title={p.reason || 'Petty cash request'}
              sub={[p.requested_by, p.team_id, p.job_order_id, fmtDate(p.requested_at)].filter(Boolean).join(' · ')}
              amount={`$${fmtMoney(p.amount)}`} badge={statusInfo(p.status).label}
            />
          ))}
          <div className="hub-dsub">Purchase requests<span className="hub-dsub-n">{pu.length}</span></div>
          {pu.length === 0 ? <Empty>None in range.</Empty> : pu.map((p) => (
            <Rec
              key={`pu${p.id}`} color={statusInfo(p.status).color}
              title={`${p.item_name}${p.quantity > 1 ? ` ×${p.quantity}` : ''}`}
              sub={[p.vendor, p.priority, fmtDate(p.requested_at)].filter(Boolean).join(' · ')}
              amount={`$${fmtMoney(p.estimated_cost)}`} badge={statusInfo(p.status).label}
            />
          ))}
        </>
      );
    } else if (rkey === 'fsr') {
      const items = recs?.items || [];
      list = (
        <>
          <div className="hub-dsub hub-dsub-records">Reports<span className="hub-dsub-n">{items.length}</span></div>
          {items.length === 0 ? <Empty>No reports in range.</Empty> : items.map((it) => (
            <Rec
              key={`${it.region}-${it.id}`} color={regionMeta(it.region).color}
              title={it.clientName || it.siteName || 'Field service report'}
              sub={[it.siteName && it.clientName ? it.siteName : null, fmtDate(it.date)].filter(Boolean).join(' · ')}
              badge={regionMeta(it.region).short}
              onClick={() => onOpenFsr && onOpenFsr(it)}
            />
          ))}
        </>
      );
    } else if (rkey === 'attendance') {
      const present = recs?.present || [];
      const absent = recs?.absent || [];
      list = (
        <>
          <div className="hub-dsub">Signed in<span className="hub-dsub-n">{present.length}</span></div>
          {present.length === 0 ? <Empty>Nobody signed in this range.</Empty> : present.map((p) => (
            <Rec
              key={`p${p.fo_id || p.fo_name}`} color={statusInfo(p.last_status).color}
              title={p.fo_name || 'Field officer'}
              sub={[p.days ? `${p.days} day${p.days > 1 ? 's' : ''}` : null, p.last_in ? `last in ${fmtDateTime(p.last_in)}` : null].filter(Boolean).join(' · ')}
              badge={statusInfo(p.last_status).label}
            />
          ))}
          <div className="hub-dsub">Not signed in<span className="hub-dsub-n">{absent.length}</span></div>
          {absent.length === 0 ? <Empty>Everyone on the roster signed in.</Empty> : absent.map((u) => (
            <Rec key={`a${u.user_id}`} color={C.amber} title={u.name} sub={roleLabel(u)} badge="Absent" />
          ))}
        </>
      );
    } else { // compliance | ehs
      const done = recs?.done || [];
      const notDone = recs?.notDone || [];
      list = (
        <>
          <div className="hub-dsub">Completed<span className="hub-dsub-n">{done.length}</span></div>
          {done.length === 0 ? <Empty>Nobody completed it in range.</Empty> : done.map((u) => (
            <Rec
              key={`d${u.user_id}`} color={C.green} title={u.name}
              sub={[roleLabel(u), u.last_date ? `last ${u.last_date}` : null, u.n > 1 ? `×${u.n}` : null].filter(Boolean).join(' · ')}
              badge="Done"
            />
          ))}
          <div className="hub-dsub">Not completed<span className="hub-dsub-n">{notDone.length}</span></div>
          {notDone.length === 0 ? <Empty>Everyone on the roster completed it.</Empty> : notDone.map((u) => (
            <Rec key={`n${u.user_id}`} color={C.red} title={u.name} sub={roleLabel(u)} badge="Missing" />
          ))}
        </>
      );
    }
    records = (
      <>
        <div className="hub-dsub hub-dsub-records">Records</div>
        {list}
      </>
    );
  }

  return <div className="hub-detail">{breakdown}{records}</div>;
}

export default function DetailCanvas({ selection, reports, range, onClose }) {
  const [date, setDate] = useState(new Date());
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [recs, setRecs] = useState(null);
  const [recLoading, setRecLoading] = useState(false);
  const [recError, setRecError] = useState(false);

  const [openFsr, setOpenFsr] = useState(null);
  const [fsrDetail, setFsrDetail] = useState(null);
  const [fsrLoading, setFsrLoading] = useState(false);
  const [fsrError, setFsrError] = useState(false);

  const isClient = selection?.kind === 'client';
  const isReport = selection?.kind === 'report';
  const meta = regionMeta(selection?.region);
  const rmeta = isReport ? REPORT_META[selection.report] : null;
  const dateStr = useMemo(() => format(date, 'yyyy-MM-dd'), [date]);

  // client mode: that client's jobs on the chosen day
  useEffect(() => {
    if (!isClient || !selection) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchJobs({ region: selection.region, clientId: selection.id, from: dateStr, to: dateStr });
        if (!cancelled) setJobs(data.jobs || []);
      } catch (e) {
        if (!cancelled) setJobs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isClient, selection, dateStr]);

  // report mode: drill-down records for the record-capable tiles (guarded)
  useEffect(() => {
    if (!isReport) return undefined;
    const key = selection.report;
    if (!RECORD_KEYS.includes(key)) { setRecs(null); setRecError(false); setRecLoading(false); return undefined; }
    let cancelled = false;
    (async () => {
      setRecLoading(true); setRecError(false); setRecs(null);
      try {
        const data = await fetchReportItems(key, { from: range?.from, to: range?.to });
        if (!cancelled) setRecs(data || {});
      } catch (e) {
        if (!cancelled) setRecError(true);
      } finally {
        if (!cancelled) setRecLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isReport, selection, range]);

  // reset the opened FSR whenever the selection changes (new tile / closed drawer)
  useEffect(() => { setOpenFsr(null); }, [selection]);

  // fetch a single FSR's detail when one is opened
  useEffect(() => {
    if (!openFsr) { setFsrDetail(null); setFsrError(false); setFsrLoading(false); return undefined; }
    let cancelled = false;
    (async () => {
      setFsrLoading(true); setFsrError(false); setFsrDetail(null);
      try {
        const d = await fetchFsrDetail(openFsr.region, openFsr.id);
        if (!cancelled) setFsrDetail(d || null);
      } catch (e) {
        if (!cancelled) setFsrError(true);
      } finally {
        if (!cancelled) setFsrLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [openFsr]);

  const headColor = isReport ? rmeta.color : meta.color;
  const title = isReport ? rmeta.title : (selection?.name || selection?.clientName || 'Detail');
  const subtitle = isReport
    ? `Reports & Data${range?.from ? ` · ${range.from} → ${range.to}` : ''}`
    : `${meta.label}${selection?.sub ? ` · ${selection.sub}` : ''}`;

  const showFsrDetail = isReport && selection.report === 'fsr' && openFsr;

  return (
    <AnimatePresence>
      {selection && (
        <>
          <motion.div
            className="hub-drawer-scrim"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="hub-drawer"
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 280, damping: 32 }}
          >
            <div className="hub-drawer-head">
              <span className="hub-res-icon" style={{ background: `${headColor}22`, color: headColor, width: 40, height: 40 }}>
                {isReport ? <rmeta.Icon size={18} /> : <RegionIcon region={selection.region} />}
              </span>
              <div>
                <div className="hub-drawer-title">{title}</div>
                <div className="hub-drawer-sub">{subtitle}</div>
              </div>
              <button className="hub-drawer-x" onClick={onClose}><X size={20} /></button>
            </div>

            {isClient ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontSize: 12.5, color: C.muted }}>Jobs on</span>
                  <DatePicker
                    selected={date}
                    onChange={(d) => d && setDate(d)}
                    dateFormat="EEE, MMM d, yyyy"
                    customInput={<DateInput />}
                    calendarClassName="hub-cal"
                    popperPlacement="bottom-end"
                  />
                </div>

                {loading ? (
                  <div className="hub-feed-loading"><Loader2 size={22} className="hub-spin" /> Loading…</div>
                ) : jobs.length === 0 ? (
                  <div className="hub-feed-empty"><Inbox size={26} /> <span>No job on this day.</span></div>
                ) : (
                  <AnimatePresence>
                    {jobs.map((j) => <JobCard key={`${j.region}-${j.id}`} job={j} />)}
                  </AnimatePresence>
                )}
              </>
            ) : isReport ? (
              showFsrDetail ? (
                <FsrDetailView item={openFsr} detail={fsrDetail} loading={fsrLoading} error={fsrError} onBack={() => setOpenFsr(null)} />
              ) : (
                <ReportBody rkey={selection.report} reports={reports} recs={recs} recLoading={recLoading} recError={recError} onOpenFsr={setOpenFsr} />
              )
            ) : (
              <JobCard job={selection.job || selection} />
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
